import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Public pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ProductsPage from './pages/public/ProductsPage';
import ProductDetailPage from './pages/public/ProductDetailPage';
import ContactPage from './pages/public/ContactPage';
import GalleryPage from './pages/public/GalleryPage';
import PublicLayout from './components/Layout/PublicLayout';

// Auth
import LoginPage from './pages/auth/LoginPage';

// Admin
import AdminLayout from './components/Layout/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminCustomers from './pages/admin/Customers';
import AdminOrders from './pages/admin/Orders';
import AdminBilling from './pages/admin/Billing';
import AdminInventory from './pages/admin/Inventory';
import AdminEmployees from './pages/admin/Employees';
import AdminReports from './pages/admin/Reports';
import AdminUsers from './pages/admin/Users';
import AdminGallery from './pages/admin/Gallery';

// Staff
import StaffLayout from './components/Layout/StaffLayout';
import StaffDashboard from './pages/staff/Dashboard';
import StaffDeliveries from './pages/staff/Deliveries';

// Customer
import CustomerLayout from './components/Layout/CustomerLayout';
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerOrders from './pages/customer/Orders';
import CustomerBills from './pages/customer/Bills';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-center"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'staff') return <Navigate to="/staff" replace />;
    return <Navigate to="/portal" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-center"><div className="spinner"/></div>;
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'staff') return <Navigate to="/staff" replace />;
    return <Navigate to="/portal" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public website */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:slug" element={<ProductDetailPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="gallery" element={<GalleryPage />} />
      </Route>

      {/* Auth */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

      {/* Admin CRM */}
      <Route path="/admin" element={<PrivateRoute roles={['admin']}><AdminLayout /></PrivateRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="employees" element={<AdminEmployees />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="gallery" element={<AdminGallery />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

      {/* Staff portal */}
      <Route path="/staff" element={<PrivateRoute roles={['staff']}><StaffLayout /></PrivateRoute>}>
        <Route index element={<StaffDashboard />} />
        <Route path="deliveries" element={<StaffDeliveries />} />
      </Route>

      {/* Customer portal */}
      <Route path="/portal" element={<PrivateRoute roles={['customer']}><CustomerLayout /></PrivateRoute>}>
        <Route index element={<CustomerDashboard />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="bills" element={<CustomerBills />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </AuthProvider>
    </BrowserRouter>
  );
}
